import { Link } from '../../components/base/Link';
import { asset } from '../../lib/assets';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function About() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* 首屏：真实成片做底，三条可核实的事实 */}
      <section className="relative overflow-hidden bg-brand-navy py-20">
        <div className="absolute inset-0">
          <img src={asset('/images/real/market-after.webp')} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-brand-navy/80 to-brand-navy"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl">{t('aboutNew.heroTitle')}</h1>
            <p className="text-xl leading-relaxed text-blue-100">{t('aboutNew.heroSubtitle')}</p>
            <ul className="mt-8 flex flex-wrap justify-center gap-3">
              {(['heroChip1', 'heroChip2', 'heroChip3'] as const).map(key => (
                <li key={key} className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-blue-50">{t(`aboutNew.${key}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 公司简介 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('aboutNew.companyTitle')}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  {t('aboutNew.companyP1')}
                </p>
                <p>
                  {t('aboutNew.companyP2')}
                </p>
                <p>
                  {t('aboutNew.companyP3')}
                </p>
              </div>
            </div>
            <figure>
              <img
                src={asset('/images/real/editor-objects.webp')}
                alt={t('aboutNew.companyImageCaption')}
                loading="lazy"
                className="w-full rounded-xl shadow-lg border border-gray-200"
              />
              <figcaption className="mt-3 text-center text-sm text-gray-500">{t('aboutNew.companyImageCaption')}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* 我们怎么做 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('aboutNew.approachTitle')}</h2>
            <p className="text-lg text-gray-600">{t('aboutNew.approachDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {([['approach1', 'ri-flow-chart'], ['approach2', 'ri-database-2-line'], ['approach3', 'ri-user-follow-line'], ['approach4', 'ri-line-chart-line']] as const).map(([key, icon]) => (
              <article key={key} className="bg-white rounded-2xl shadow-card p-6 flex items-start">
                <span className="mr-4 mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700"><i className={`${icon} text-xl`}></i></span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t(`aboutNew.${key}Title`)}</h3>
                  <p className="mt-2 text-gray-600 leading-relaxed">{t(`aboutNew.${key}Desc`)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 发展历程 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-10">{t('aboutNew.timelineTitle')}</h2>
          <ol className="relative border-l-2 border-brand-100 ml-3 space-y-8 md:ml-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10 md:space-y-0 md:border-l-0">
            {(['tl1', 'tl2', 'tl3', 'tl4', 'tl5', 'tl6'] as const).map(key => (
              <li key={key} className="relative pl-8 md:pl-0 md:border-t-2 md:border-brand-100 md:pt-5">
                <span className="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white bg-brand-600 shadow md:-top-[9px] md:left-0"></span>
                <p className="text-sm font-semibold text-brand-700">{t(`aboutNew.${key}Date`)}</p>
                <p className="mt-1 text-gray-700 leading-relaxed">{t(`aboutNew.${key}Text`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 数据与安全 */}
      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-3">{t('aboutNew.dataTitle')}</h2>
            <p className="text-blue-100">{t('aboutNew.dataDesc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {(['data1', 'data2', 'data3', 'data4'] as const).map(key => (
              <div key={key} className="rounded-xl border border-white/10 bg-white/5 p-5 flex items-start">
                <i className="ri-shield-check-line text-brand-cyan text-xl mr-3 mt-0.5"></i>
                <p className="text-blue-50 leading-relaxed">{t(`aboutNew.${key}`)}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-blue-200">
            <Link to="/privacy-policy" className="underline hover:text-white">{t('aboutNew.resourcePrivacy')}</Link>
            <span className="mx-2">·</span>
            <Link to="/terms-of-service" className="underline hover:text-white">{t('footer.termsOfService')}</Link>
          </p>
        </div>
      </section>

      {/* 公司信息与产品矩阵 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-card p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('aboutNew.infoTitle')}</h2>
            <dl className="space-y-4 text-gray-700">
              <div><dt className="text-sm text-gray-500">{t('aboutNew.infoCompany')}</dt><dd className="font-medium">郑州欧睿达信息科技有限公司</dd></div>
              <div><dt className="text-sm text-gray-500">{t('aboutNew.infoFounded')}</dt><dd className="font-medium">{t('aboutNew.infoFoundedValue')}</dd></div>
              <div><dt className="text-sm text-gray-500">{t('aboutNew.addressTitle')}</dt><dd className="font-medium">{t('aboutNew.addressValue')}</dd></div>
              <div><dt className="text-sm text-gray-500">{t('aboutNew.infoIcp')}</dt><dd className="font-medium">豫ICP备2025149205号-1</dd></div>
              <div><dt className="text-sm text-gray-500">{t('aboutNew.infoPsb')}</dt><dd className="font-medium">豫公网安备41010502006601号</dd></div>
              <div><dt className="text-sm text-gray-500">{t('aboutNew.emailTitle')}</dt><dd className="font-medium"><a className="text-brand-700 underline" href="mailto:contact@oredact.com">contact@oredact.com</a></dd></div>
            </dl>
          </div>
          <div className="lg:col-span-3 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('aboutNew.matrixTitle')}</h2>
            <article className="bg-white rounded-2xl shadow-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200"><img src={asset('/images/brand/damaya-logo.webp')} alt="" aria-hidden="true" width="40" height="40" loading="lazy" className="h-10 w-10 object-contain" /></span>
                <h3 className="text-xl font-semibold text-gray-900">{t('aboutNew.matrixDamayaTitle')}</h3>
              </div>
              <p className="mt-2 text-gray-600 leading-relaxed">{t('aboutNew.matrixDamayaDesc')}</p>
              <a href="https://damaya.cn" className="mt-3 inline-block text-brand-700 underline">damaya.cn</a>
            </article>
            <article className="bg-white rounded-2xl shadow-card p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-gray-200"><img src={'/logo.svg'} alt="" aria-hidden="true" width="40" height="40" loading="lazy" className="h-10 w-10 object-contain" /></span>
                <h3 className="text-xl font-semibold text-gray-900">{t('aboutNew.matrixApiTitle')}</h3>
              </div>
              <p className="mt-2 text-gray-600 leading-relaxed">{t('aboutNew.matrixApiDesc')}</p>
              <Link to="/pricing" className="mt-3 inline-block text-brand-700 underline">{t('aboutNew.matrixApiCta')}</Link>
            </article>
            <article className="bg-white rounded-2xl shadow-card p-6">
              <h3 className="text-xl font-semibold text-gray-900">{t('aboutNew.boundaryTitle')}</h3>
              <ul className="mt-3 space-y-2 text-gray-600">
                <li className="flex items-start"><i className="ri-information-line text-brand-600 mr-2 mt-1"></i>{t('aboutNew.boundary1')}</li>
                <li className="flex items-start"><i className="ri-information-line text-brand-600 mr-2 mt-1"></i>{t('aboutNew.boundary2')}</li>
                <li className="flex items-start"><i className="ri-information-line text-brand-600 mr-2 mt-1"></i>{t('aboutNew.boundary3')}</li>
              </ul>
            </article>
          </div>
        </div>
      </section>


      {/* 联系方式 */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('aboutNew.contactTitle')}</h2>
            <p className="text-lg text-gray-600">{t('aboutNew.contactSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <article className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('aboutNew.emailTitle')}</h3>
              <a className="block text-blue-700 underline" href="mailto:contact@oredact.com">contact@oredact.com</a>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-open-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('aboutNew.resourcesTitle')}</h3>
              <a className="block text-blue-700 underline" href="https://damaya.cn/help">{t('aboutNew.resourceHelp')}</a>
              <Link className="block text-blue-700 underline" to="/pricing">{t('aboutNew.resourcePricing')}</Link>
              <Link className="block text-blue-700 underline" to="/privacy-policy">{t('aboutNew.resourcePrivacy')}</Link>
            </article>
            
            <article className="text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('aboutNew.workingHours')}</h3>
              <p className="text-gray-600">{t('aboutNew.workingDays')}</p>
              <p className="text-gray-600">{t('aboutNew.workingTime')}</p>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
